import React, { useState } from 'react';
import { Company } from '../types/Company';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useApiCall } from '../../shared/hooks/useApiCall';
import { useToast } from '../../shared/contexts/ToastContext';

interface CompanySelectionProps {
  companies: Company[];
  isLoading: boolean;
  error: string | null;
  viewMode: 'grid' | 'table';
  onViewModeChange: (mode: 'grid' | 'table') => void;
  onAddCompany: () => void;
  onCompanySelect: (company: Company) => void;
  onShowCompany: (company: Company) => void;
  onEditCompany: (company: Company, event: React.MouseEvent) => void;
  onRemoveCompany: (company: Company, event: React.MouseEvent) => void;
  onRefreshCompanies: () => void;
}

const CompanySelection: React.FC<CompanySelectionProps> = ({
  companies,
  isLoading,
  error,
  viewMode,
  onViewModeChange,
  onAddCompany,
  onCompanySelect,
  onShowCompany,
  onEditCompany,
  onRemoveCompany,
  onRefreshCompanies
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [filterByUser, setFilterByUser] = useState<string>('');
  
  const { user, canManageCompanies } = useAuth();
  const { apiCall } = useApiCall();
  const { success, error: showError } = useToast();

  // Get unique users for filtering
  const uniqueUsers = Array.from(
    new Set(
      companies
        .filter(company => company.username)
        .map(company => company.username)
    )
  ).sort();

  // Filter and sort companies
  const filteredCompanies = companies
    .filter(company => {
      // User filter
      if (filterByUser && company.username !== filterByUser) {
        return false;
      }
      
      // Search filter
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase().trim();
    const companyName = company.name?.toLowerCase() || '';
    const companyCode = company.company_code?.toLowerCase() || '';
    
    // Tam eşleşme kontrolü (en yüksek öncelik)
    if (companyName === searchLower || companyCode === searchLower) {
      return true;
    }
    
    // Başlangıç eşleşmesi (her zaman öncelik)
    if (companyName.startsWith(searchLower) || companyCode.startsWith(searchLower)) {
      return true;
    }
    
    // Kelime başı eşleşmesi
    const nameWords = companyName.split(/\s+/);
    const codeWords = companyCode.split(/\s+/);
    
    const startsWithSearch = (word: string) => word.startsWith(searchLower);
    
    if (nameWords.some(startsWithSearch) || codeWords.some(startsWithSearch)) {
      return true;
    }
    
    // Çoklu kelime araması
    const searchWords = searchLower.split(/\s+/).filter(word => word.length >= 1);
    if (searchWords.length > 1) {
      const allWordsMatch = searchWords.every(searchWord => 
        nameWords.some(nameWord => nameWord.startsWith(searchWord)) ||
        codeWords.some(codeWord => codeWord.startsWith(searchWord)) ||
        companyName.includes(searchWord) ||
        companyCode.includes(searchWord)
      );
      if (allWordsMatch) return true;
    }
    
    // İçerik eşleşmesi (2+ karakter için)
    if (searchLower.length >= 2) {
      return companyName.includes(searchLower) || companyCode.includes(searchLower);
    }
    
    return false;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Çevirmen Header - Sadece şirket seçim ekranında */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Çevirmen
        </h1>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Header with View Toggle and Add Company Button */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 px-8 py-6 border-b border-gray-100 dark:border-gray-700 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Şirket Seçin
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Çeviri işlemlerine başlamak için bir şirket seçin</p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Add Company Button - Only visible for admin users */}
              {canManageCompanies() && (
                <button
                  onClick={onAddCompany}
                  className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center transform hover:scale-105"
                >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Yeni Şirket Ekle
                </button>
              )}
            
              {/* View Toggle - Only when companies exist */}
              {companies.length > 0 && (
                <div className="flex bg-white dark:bg-gray-700 rounded-xl p-1 shadow-inner border border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => onViewModeChange('grid')}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      viewMode === 'grid'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md transform scale-105'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                  </svg>
                  </button>
                  <button
                    onClick={() => onViewModeChange('table')}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      viewMode === 'table'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md transform scale-105'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9m-9 4h9m-9-8h9m-9 4h9"/>
                  </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Search Bar - Only when companies exist */}
        {companies.length > 0 && (
          <div className="px-8 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex items-center gap-4">
              <div className="relative" style={{width: '50%'}}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Şirket adı veya kodu ile ara..."
                className="block w-full pl-10 pr-4 h-10 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              )}
              </div>
              
              {/* Filters and Sorting */}
              <div className="flex items-center gap-3" style={{width: '50%'}}>
                {/* Sort Dropdown */}
                <div className="relative" style={{width: '50%'}}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest')}
                    className="appearance-none w-full h-10 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="newest">En Yeni</option>
                    <option value="oldest">En Eski</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>

                {/* User Filter Dropdown */}
                {uniqueUsers.length > 0 && (
                  <div className="relative" style={{width: '50%'}}>
                    <select
                      value={filterByUser}
                      onChange={(e) => setFilterByUser(e.target.value)}
                      className="appearance-none w-full h-10 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <option value="">Tüm Kullanıcılar</option>
                      {uniqueUsers.map(username => (
                        <option key={username} value={username}>
                          @{username}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Results Info */}
            {searchTerm && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {filteredCompanies.length} şirket bulundu
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Şirket verileri yükleniyor...
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Lütfen bekleyin, veriler webhook'tan çekiliyor.
            </p>
          </div>
        )}
        
        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Veri yükleme hatası
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <button
              onClick={onRefreshCompanies}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        )}
        
        {!isLoading && !error && companies.length === 0 ? (
          // No companies - show add company prompt
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Henüz şirket eklenmemiş
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Çeviri işlemlerine başlamak için önce bir şirket ekleyin. Yukarıdaki "Yeni Şirket Ekle" butonunu kullanabilirsiniz.
            </p>
          </div>
        ) : !isLoading && !error && companies.length > 0 ? (
          // Companies exist - show selection in grid or table view
          <div className="p-8">
            {filteredCompanies.length === 0 ? (
              // No search results
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Arama sonucu bulunamadı
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  "{searchTerm}" için eşleşen şirket bulunamadı.
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Aramayı temizle
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCompanies.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => onCompanySelect(company)}
                    className="bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-2xl p-6 cursor-pointer hover:shadow-2xl hover:shadow-blue-500/10 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-300 group relative overflow-hidden transform hover:-translate-y-1 hover:scale-[1.02]"
                  >
                    {/* Header with icon and actions */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:from-blue-600 group-hover:to-indigo-700 transition-all duration-300 flex-shrink-0 shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
                        </svg>
                      </div>
                      {canManageCompanies() && (
                        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={(e) => onEditCompany(company, e)}
                            className="p-2 text-emerald-600 hover:text-white hover:bg-emerald-600 dark:text-emerald-400 dark:hover:text-white dark:hover:bg-emerald-500 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                            title="Şirketi Düzenle"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          {/* Delete button - Only visible for admin users */}
                          <button
                            onClick={(e) => onRemoveCompany(company, e)}
                            className="p-2 text-red-600 hover:text-white hover:bg-red-600 dark:text-red-400 dark:hover:text-white dark:hover:bg-red-500 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                            title="Şirketi Sil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Company info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate">
                          {company.name}
                        </h3>
                        {company.company_code && (
                          <span className="text-xs text-gray-600 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full font-mono">
                            {company.company_code}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(company.createdAt).toLocaleDateString('tr-TR')}
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {company.created_by_username && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Yaratan:</span>
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                              @{company.created_by_username}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Atanan:</span>
                          {company.assigned_usernames ? (
                            (() => {
                              const usernames = company.assigned_usernames.split(',').map(u => u.trim());
                              const firstUser = usernames[0];
                              const remainingCount = usernames.length - 1;
                              
                              return (
                                <>
                                  {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                                  {remainingCount === 0 ? (
                                    <div className="relative group">
                                      <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                        @{firstUser}
                                      </span>
                                      {/* Hover Tooltip - Tek kullanıcı */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Atanan Kullanıcı
                                        </div>
                                        <div className="text-[10px] text-center">
                                          @{firstUser}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                      @{firstUser}
                                    </span>
                                  )}
                                  
                                  {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                                  {remainingCount > 0 && (
                                    <div className="relative group">
                                      <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                        +{remainingCount}
                                      </span>
                                      {/* Hover Tooltip - TÜM kullanıcılar */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Tüm Atanan Kullanıcılar
                                        </div>
                                        <div className="space-y-1">
                                          {usernames.map((username, idx) => (
                                            <div key={idx} className="text-[10px]">
                                              @{username}
                                            </div>
                                          ))}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* File indicators */}
                    {/* NORMALIZE FIELD - Update condition to include normalize: (company.glossaryFileName || company.glossary_file || company.normalizeFileName || company.normalize_file) */}
                    {(company.glossaryFileName || company.glossary_file) && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                        <div className="flex flex-wrap gap-2">
                          {(company.glossaryFileName || company.glossary_file) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                              Glossary
                            </span>
                          )}
                          {/* NORMALIZE FIELD - Uncomment to enable
                          {(company.normalizeFileName || company.normalize_file) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                              Normalize
                            </span>
                          )}
                          */}
                        </div>
                      </div>
                    )}
                    
                    {/* Seç Butonu */}
                    <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-600">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCompanySelect(company);
                        }}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Seç
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Table View
              <div className="bg-white dark:bg-gray-700 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-xl overflow-hidden">
                <table className="w-full divide-y divide-gray-100 dark:divide-gray-600">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{width: '30%'}}>
                        Şirket
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{width: '12%'}}>
                        Tarih
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{width: '28%'}}>
                        Kullanıcılar
                      </th>
                      <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{width: '30%'}}>
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-600 bg-white dark:bg-gray-700">
                      {filteredCompanies.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Arama sonucu bulunamadı
                              </h3>
                              <p className="text-gray-600 dark:text-gray-400 mb-4">
                                "{searchTerm}" için eşleşen şirket bulunamadı.
                              </p>
                              <button
                                onClick={() => setSearchTerm('')}
                                className="text-blue-600 hover:text-blue-700 font-medium"
                              >
                                Aramayı temizle
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredCompanies.map((company) => (
                        <tr
                          key={company.id}
                          onClick={() => onCompanySelect(company)}
                          className="transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-gray-600 dark:hover:to-gray-600 cursor-pointer hover:shadow-md"
                        >
                          <td className="px-6 py-5">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"/>
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {company.name}
                                  </div>
                                  {company.company_code && (
                                    <span className="text-xs text-gray-600 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full font-mono">
                                      {company.company_code}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-4 text-xs text-gray-500 dark:text-gray-400">
                            {new Date(company.createdAt).toLocaleDateString('tr-TR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-1.5">
                              {company.created_by_username && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[50px]">Yaratan:</span>
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                    @{company.created_by_username}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[50px]">Atanan:</span>
                                {company.assigned_usernames ? (
                                  (() => {
                                    const usernames = company.assigned_usernames.split(',').map(u => u.trim());
                                    const firstUser = usernames[0];
                                    const remainingCount = usernames.length - 1;
                                    
                                    return (
                                      <>
                                        {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                                        {remainingCount === 0 ? (
                                          <div className="relative group">
                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                              @{firstUser}
                                            </span>
                                            {/* Hover Tooltip - Tek kullanıcı */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                              <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                                Atanan Kullanıcı
                                              </div>
                                              <div className="text-[10px] text-center">
                                                @{firstUser}
                                              </div>
                                              {/* Arrow */}
                                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                            @{firstUser}
                                          </span>
                                        )}
                                        
                                        {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                                        {remainingCount > 0 && (
                                          <div className="relative group">
                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                              +{remainingCount}
                                            </span>
                                            {/* Hover Tooltip - TÜM kullanıcılar */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                              <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                                Tüm Atanan Kullanıcılar
                                              </div>
                                              <div className="space-y-1">
                                                {usernames.map((username, idx) => (
                                                  <div key={idx} className="text-[10px]">
                                                    @{username}
                                                  </div>
                                                ))}
                                              </div>
                                              {/* Arrow */}
                                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()
                                ) : (
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">-</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-5 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCompanySelect(company);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 flex items-center shadow-md hover:shadow-lg transform hover:scale-105"
                                title="Seç"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                Seç
                              </button>
                              {canManageCompanies() && (
                                <>
                                  <button
                                    onClick={(e) => onEditCompany(company, e)}
                                    className="p-2.5 text-emerald-600 hover:text-white hover:bg-emerald-600 dark:text-emerald-400 dark:hover:text-white dark:hover:bg-emerald-500 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                                    title="Düzenle"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                    </svg>
                                  </button>
                                  {/* Delete button - Only visible for admin users */}
                                  <button
                                    onClick={(e) => onRemoveCompany(company, e)}
                                    className="p-2.5 text-red-600 hover:text-white hover:bg-red-600 dark:text-red-400 dark:hover:text-white dark:hover:bg-red-500 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                                    title="Sil"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompanySelection;
